import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { KnowledgeSet } from '@/types/knowledge';
import { AlertTriangle, BookOpen, Edit, Plus, Trash } from 'lucide-react';
import { useState } from 'react';
import { KnowledgeSetDetail } from './KnowledgeSetDetail';
import { KnowledgeSetEditDialog } from './KnowledgeSetEditDialog';

// Mock data for knowledge sets
const mockKnowledgeSets: KnowledgeSet[] = [
    {
        id: '1',
        name: 'Product Documentation',
        description: 'Documentation for all products',
        prompt_direction: 'Use this knowledge set to answer product-related questions',
        created_at: '2024-01-15T10:00:00Z',
        updated_at: '2024-01-20T14:30:00Z',
    },
    {
        id: '2',
        name: 'Company Policies',
        description: 'Internal company policies and procedures',
        created_at: '2024-01-10T09:00:00Z',
        updated_at: '2024-01-18T11:00:00Z',
    },
    {
        id: '3',
        name: 'Technical Knowledge Base',
        description: 'Technical documentation and guides',
        prompt_direction: 'Reference technical documentation when answering technical questions',
        created_at: '2024-01-12T08:00:00Z',
        updated_at: '2024-01-19T16:00:00Z',
    },
];

export const KnowledgeCenter = () => {
    const [knowledgeSets, setKnowledgeSets] = useState<KnowledgeSet[]>(mockKnowledgeSets);
    const [selectedSet, setSelectedSet] = useState<KnowledgeSet | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingSet, setEditingSet] = useState<KnowledgeSet | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [setToDelete, setSetToDelete] = useState<KnowledgeSet | null>(null);

    const handleCreateNew = () => {
        setEditingSet(null);
        setIsEditDialogOpen(true);
    };

    const handleEdit = (set: KnowledgeSet) => {
        setEditingSet(set);
        setIsEditDialogOpen(true);
    };

    const handleSave = (setData: Partial<KnowledgeSet>) => {
        if (editingSet) {
            // Update existing set
            setKnowledgeSets(
                knowledgeSets.map((set) =>
                    set.id === editingSet.id
                        ? {
                            ...set,
                            ...setData,
                            updated_at: new Date().toISOString(),
                        }
                        : set
                )
            );
        } else {
            // Create new set
            const newSet: KnowledgeSet = {
                id: Date.now().toString(),
                name: setData.name || '',
                description: setData.description,
                prompt_direction: setData.prompt_direction,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            };
            setKnowledgeSets([...knowledgeSets, newSet]);
        }
        setIsEditDialogOpen(false);
        setEditingSet(null);
    };

    const handleDelete = (set: KnowledgeSet) => {
        setSetToDelete(set);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (setToDelete) {
            setKnowledgeSets(knowledgeSets.filter((set) => set.id !== setToDelete.id));
            if (selectedSet?.id === setToDelete.id) {
                setSelectedSet(null);
            }
            setDeleteDialogOpen(false);
            setSetToDelete(null);
        }
    };

    const handleViewDetail = (set: KnowledgeSet) => {
        setSelectedSet(set);
    };

    const handleBackToList = () => {
        setSelectedSet(null);
    };

    if (selectedSet) {
        return (
            <KnowledgeSetDetail knowledgeSet={selectedSet} onBack={handleBackToList} />
        );
    }

    return (
        <div className="w-full">
            <div
                className="mb-4 px-4 py-2 rounded text-center font-semibold bg-background border border-secondary text-primary"
            >
                <span role="img" aria-label="Demo">🛈</span> This Knowledge Center is running in <strong>demo mode</strong>. Data is sampled for demonstration purposes and actions will not have any real effect.
            </div>
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center mb-4">
                    <BookOpen className="mr-3 text-primary" size={32} />
                    <div>
                        <h1 className="text-3xl font-bold text-foreground">
                            Knowledge Center
                        </h1>
                        <p className="text-lg text-muted-foreground">
                            Manage knowledge sets and content sources
                        </p>
                    </div>
                </div>

                <button
                    onClick={handleCreateNew}
                    className="font-medium py-2 px-4 rounded-md flex items-center transition-colors bg-primary text-primary-foreground hover:bg-primary/90"
                >
                    <Plus size={18} className="mr-2" />
                    Create Knowledge Set
                </button>
            </div>

            {/* Knowledge Sets Table */}
            <div
                className="overflow-x-auto rounded-lg shadow bg-background"
            >
                <table
                    className="min-w-full divide-y bg-background"
                >
                    <thead>
                        <tr>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                            >
                                Name
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell text-muted-foreground"
                            >
                                Description
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell text-muted-foreground"
                            >
                                Created
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell text-muted-foreground"
                            >
                                Updated
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-background divide-y divide-border w-full">
                        {knowledgeSets.length === 0 ? (
                            <tr className="w-full">
                                <td colSpan={5} className="px-6 py-8 text-center w-full">
                                    No knowledge sets have been created yet. Use the "Create Knowledge Set" button to
                                    create one.
                                </td>
                            </tr>
                        ) : (
                            knowledgeSets.map((set) => (
                                <tr
                                    key={set.id}
                                    className="hover:bg-muted/50 cursor-pointer transition-colors"
                                    onClick={() => handleViewDetail(set)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-foreground">
                                            {set.name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                                        <div
                                            className="text-sm truncate max-w-xs text-muted-foreground"
                                        >
                                            {set.description || 'No description'}
                                        </div>
                                    </td>
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell text-muted-foreground"
                                    >
                                        {new Date(set.created_at).toLocaleDateString()}
                                    </td>
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell text-muted-foreground"
                                    >
                                        {new Date(set.updated_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end items-center gap-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEdit(set);
                                                }}
                                                className="transition-colors text-muted-foreground hover:text-primary"
                                            >
                                                <Edit size={18} />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(set);
                                                }}
                                                className="transition-colors text-muted-foreground hover:text-destructive"
                                            >
                                                <Trash size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit/Create Dialog */}
            <KnowledgeSetEditDialog
                isOpen={isEditDialogOpen}
                onClose={() => {
                    setIsEditDialogOpen(false);
                    setEditingSet(null);
                }}
                onSave={handleSave}
                knowledgeSet={editingSet}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center">
                            <AlertTriangle className="mr-2 text-destructive" size={20} />
                            Confirm Delete
                        </DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete the knowledge set{' '}
                            <span className="font-semibold">{setToDelete?.name}</span>? This action cannot be
                            undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <button
                            onClick={() => setDeleteDialogOpen(false)}
                            className="px-4 py-2 border rounded-md transition-colors border-input hover:bg-muted text-foreground"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={confirmDelete}
                            className="px-4 py-2 rounded-md transition-colors bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
